import { Injectable } from '@angular/core';
import { StudioTextAnimationStep } from '../..';
import { LineAnimation } from '../../../../animation/text-animation.service';
import {
  GradientAnimationMetaData,
  TextAnimationMetaData,
  TranslateTextAnimationMetaData,
  ZoomAnimationMetaData,
} from '../animation.types';
import { TEXT_ANIMATION_PRESETS } from './studio-text-animation-presets';

@Injectable({ providedIn: 'root' })
export class StudioTextAnimationMetaService {
  animationPresets = TEXT_ANIMATION_PRESETS;
  convertAnimationMetaData(
    target: string,
    metas: TextAnimationMetaData[],
  ): StudioTextAnimationStep[] {
    let wordOffset = 0;
    const steps = metas.map(meta => {
      const step = this.buildStep(target, meta);

      if (meta.segmentMode != null) {
        const start = meta.segmentMode === 'additive' ? 0 : wordOffset;
        const end: number | undefined =
          meta.numberOfWords != null
            ? wordOffset + meta.numberOfWords
            : undefined;
        step.line = {
          segment: [start, end] as [number, number?],
          ...(meta.segmentMode === 'additive' && { align: 'continuous' }),
        } as LineAnimation;
        wordOffset += meta.numberOfWords ?? 0;
      }
      if ((meta as TextAnimationMetaData).align) {
        step.line ||= {};
        step.line.align = (meta as TextAnimationMetaData).align;
      }

      return step;
    });

    // Chain steps via .next
    for (let i = steps.length - 2; i >= 0; i--) {
      steps[i].next = steps[i + 1];
    }

    return steps.length > 0 ? [steps[0]] : [];
  }

  buildStep(
    target: string,
    meta: TextAnimationMetaData,
  ): StudioTextAnimationStep {
    const step: StudioTextAnimationStep = {
      target,
      type: 'appear',
      duration: meta.duration ?? 1,
      ...(meta.hold != null && { hold: meta.hold }),
    };

    const zoomMeta = meta as ZoomAnimationMetaData;
    const translateMeta = meta as TranslateTextAnimationMetaData;

    switch (meta.by) {
      case 'line':
        step.line = { ...(step.line || {}) } as LineAnimation;
        break;
    }

    if (zoomMeta.zoom) {
      step.zoom = { from: zoomMeta.zoom[0], to: zoomMeta.zoom[1] };
    }

    if (translateMeta.translate) {
      step.translate = {
        from: translateMeta.translate,
        ...(translateMeta.mask !== undefined && { mask: translateMeta.mask }),
      };
    }

    if (zoomMeta.by) {
      const { item } = zoomMeta;
      const itemStep: any = {
        ...(item.overlap !== undefined && { overlap: item.overlap }),
        ...(item.zoom !== undefined && { zoom: item.zoom }),
        ...(item.translate !== undefined && {
          translate: { from: item.translate },
        }),
      };
      if (zoomMeta.by === 'word') {
        step.word = itemStep;
      } else {
        step.letter = itemStep;
      }
    }

    const gradientMeta = meta as GradientAnimationMetaData;
    if (gradientMeta.gradient) {
      step.colorize = {
        wordIndex: gradientMeta.wordIndex ?? 0,
        gradient: gradientMeta.gradient,
      };
    }

    return step;
  }
}
